import type { Database } from '@/types'
import { create } from 'zustand'

export type TierType = Database['public']['Enums']['tier_type']

export const GENETICS_KIT_CENTS = 55000

export type Alternative = {
  id: string
  tier: TierType
  name: string
  description: string
  price_cents: number
  dosagem: string
  posologia: string
}

export type ProtocolItem = {
  id: string
  product_id: string
  sort_order: number
  name: string
  description: string | null
  alternatives: Alternative[]
}

export type Protocol = {
  id: string
  title: string
  description: string | null
  has_genetics_kit: boolean
  created_at: string
  items: ProtocolItem[]
}

type ProtocolStore = {
  protocol: Protocol | null
  loading: boolean
  error: string | null
  selections: Record<string, TierType>

  reset: () => void
  setProtocol: (protocol: Protocol) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  selectTier: (productId: string, tier: TierType) => void
  getSelectedAlternative: (productId: string) => Alternative | undefined
  getTotal: () => number
  getCartItems: () => Array<{ item: ProtocolItem; alternative: Alternative }>
  isCartComplete: () => boolean
}

export const useProtocolStore = create<ProtocolStore>((set, get) => ({
  protocol: null,
  loading: true,
  error: null,
  selections: {},

  reset: () => set({ protocol: null, selections: {}, loading: true, error: null }),

  setProtocol: (protocol) =>
    set((state) => {
      const nextSelections: Record<string, TierType> = {}
      const sameProtocol = state.protocol?.id === protocol.id

      protocol.items.forEach((item) => {
        const tiers = item.alternatives.map((a) => a.tier)
        const selectionKey = item.product_id
        const previousTier = sameProtocol ? state.selections[selectionKey] : undefined

        if (previousTier && tiers.includes(previousTier)) {
          nextSelections[selectionKey] = previousTier
          return
        }

        nextSelections[selectionKey] = tiers.includes('recomendado')
          ? 'recomendado'
          : tiers.includes('intermediaria')
            ? 'intermediaria'
            : tiers.includes('premium')
              ? 'premium'
              : 'custo_beneficio'
      })

      return { protocol, selections: nextSelections }
    }),

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  selectTier: (productId, tier) =>
    set((state) => ({
      selections: { ...state.selections, [productId]: tier },
    })),

  getSelectedAlternative: (productId) => {
    const { protocol, selections } = get()
    if (!protocol) return undefined
    const item = protocol.items.find((i) => i.product_id === productId)
    if (!item) return undefined
    return item.alternatives.find((a) => a.tier === selections[productId])
  },

  getTotal: () => {
    const { protocol, selections } = get()
    if (!protocol) return 0
    return protocol.items.reduce((sum, item) => {
      const alt = item.alternatives.find((a) => a.tier === selections[item.product_id])
      return sum + (alt?.price_cents ?? 0)
    }, 0)
  },

  getCartItems: () => {
    const { protocol, selections } = get()
    if (!protocol) return []
    return protocol.items
      .map((item) => {
        const alt = item.alternatives.find((a) => a.tier === selections[item.product_id])
        return alt ? { item, alternative: alt } : null
      })
      .filter((x): x is { item: ProtocolItem; alternative: Alternative } => x !== null)
  },

  isCartComplete: () => {
    const { protocol, selections } = get()
    if (!protocol) return false
    if (protocol.items.length === 0 && protocol.has_genetics_kit) return true
    return protocol.items.every((item) => selections[item.product_id])
  },
}))
